import { computed, effect, inject, Injectable, signal } from '@angular/core';
import { CartCharges, CartItem, CartItemType, CartSectionType } from '../../../../../../models/order/workflow/order-cart.model';
import { ProductType } from '../../../../../../models/order/order.model';
import { VideoProductResponseService } from '../../order-api-response/video-pd-response.service';
import { VideoCartData } from '../../../../../../models/order/workflow/video/video-cart.model';
import { CartItemDisclosure, Choice, FlattenCustomization } from '../../../../../../models/order/api-models/order-pd-response.model';
import { OrderQualificationResponseService } from '../../order-api-response/order-oq-response.service';
import { FeatureFlagService } from '../../../../../../services/feature-flag.service';
import { OrderApiResponseStateService } from '../../order-api-response/order-api-response-state.service';


@Injectable({ providedIn: 'root' })
export class VideoCommercialSatelliteCartStateService {
    private videoPdResponseService = inject(VideoProductResponseService);
    private orderApiResponseStateService = inject(OrderApiResponseStateService);
    private oqResponseService = inject(OrderQualificationResponseService);
    private selectedCustomizations: FlattenCustomization[] = [];
    private featureFlagService = inject(FeatureFlagService);
    private _cart = signal<VideoCartData | null>(null);
    private cartOrderNumber: string | null = null;
    private hasInitializedCart = false;
    cart = this._cart.asReadonly();
    constructor() {
        effect(() => {
            const orderNumber = this.orderApiResponseStateService.orderNumber();
            if (this.cartOrderNumber !== orderNumber) {
                this.cartOrderNumber = orderNumber;
                this.hasInitializedCart = false;
            }

            this.selectedCustomizations = [];
            const videoComponent = this.videoPdResponseService.getDTVProductComponent();
            const updateOfferResponse = this.videoPdResponseService.updateOfferResponse();
            const oqResponse = this.oqResponseService.videoOQComponent();
            // Keep the first cart hidden until equipment is submitted, then allow package replacements.
            if (videoComponent && (updateOfferResponse || oqResponse)) {
                this.hasInitializedCart = true;
            }

            if (videoComponent && this.hasInitializedCart) {
                this.setupSelectedCustomizations();
                this.setupCartFromOrder();
                console.log('Video Commercial Satellite Cart:', this.cart());
            } else {
                this._cart.set(null);
            }
        });
    }

    private CartQuery(cust: FlattenCustomization): boolean {
        return cust.customization.SubGroupName == 'DTVCART' || (cust.customization.CustomizationType?.toLocaleLowerCase() === 'banner' && this.featureFlagService.isDTVCommercialBannerEnabled());
    }


    private MonthyChargesQuery(cust: FlattenCustomization): boolean {
        return cust.customization.CustomizationCode == 'DTV-CART-MONTHLYCHARGES' || (cust.customization.CustomizationType?.toLocaleLowerCase() === 'banner' && this.featureFlagService.isDTVCommercialBannerEnabled());
    }

    private TodayChargesQuery(cust: FlattenCustomization): boolean {
        return cust.customization.CustomizationCode == 'DTV-CART-TODAYSCHARGES';
    }

    private getCartDisclosureList(choice?: Choice | null): CartItemDisclosure[] {
        const parsedDisclosures = this.parseCartDisclosureArray(choice?.CartDisclosureArray);
        if (parsedDisclosures.length) {
            return parsedDisclosures;
        }

        if (choice?.CartDisclosureList?.length) {
            return choice.CartDisclosureList;
        }

        return [];
    }

    private parseCartDisclosureArray(cartDisclosureArray?: string[] | string | null): CartItemDisclosure[] {
        if (!cartDisclosureArray) {
            return [];
        }

        const entries = (Array.isArray(cartDisclosureArray) ? cartDisclosureArray : [cartDisclosureArray])
            .flatMap(disclosure => typeof disclosure === 'string' ? disclosure.split('|') : []);

        return entries.reduce<CartItemDisclosure[]>((disclosures, entry) => {
            const [label, fee] = entry.split('~').map(value => value?.trim());
            const normalizedFee = fee?.replace(/[$,]/g, '');

            if (!label || !normalizedFee || !Number.isFinite(Number(normalizedFee))) {
                return disclosures;
            }

            disclosures.push({
                Label: label,
                Fee: normalizedFee
            });

            return disclosures;
        }, []);
    }

    private setupCartFromOrder() {
        const videoCharges = this.setupVideoCharges();
        if (videoCharges) {
            const todaysCharges = this.setupTodaysCharges();
            videoCharges.todaysCharges = todaysCharges;
        }

        this._cart.set(videoCharges);
    }

    private setupSelectedCustomizations() {
        const allCustomizations = this.videoPdResponseService.getCustomizations();
        this.selectedCustomizations = allCustomizations.filter(cust => this.CartQuery(cust));
    }

    private setupTodaysCharges(): CartCharges | undefined {
        const videoOQComponent = this.oqResponseService.videoOQComponent();

        const todayCharges: CartCharges = {
            totalAmount: 0,
            items: []
        };

        if (videoOQComponent) {
            todayCharges.totalTaxAmount = videoOQComponent.TodaysCharges?.TaxAmount || '0';
            todayCharges.totalAmount += parseFloat(todayCharges.totalTaxAmount);
        }

        const todaysCustomizations = this.selectedCustomizations.filter(cust => this.TodayChargesQuery(cust));

        if (todaysCustomizations.length > 0) {
            for (const offer of todaysCustomizations) {
                for (const choice of offer.customization.Choices.Choice) {
                    const cartItem: CartItem = {
                        disclosure: choice?.CartDisclosure || '',
                        amount: choice?.Fee.Fee || 0,
                        name: choice?.Name || '',
                        choiceCode: choice?.ChoiceCode || '',
                        choice: choice,
                        customization: offer.customization,
                        metadata: null,
                        type: CartItemType.Fee,
                        cartSectionType: CartSectionType.TodaysCharges,
                        productType: ProductType.Video,
                        CartDisclosureList: this.getCartDisclosureList(choice)
                    }
                    todayCharges.items.push(cartItem);
                    todayCharges.totalAmount += cartItem.amount;
                }
            }
        }
        return todayCharges;
    }

    private setupVideoCharges(): VideoCartData | undefined {
        const videoComponent = this.videoPdResponseService.getSelectedComponent();
        const videoOQComponent = this.oqResponseService.videoOQComponent();
        const monthlyCustomizations = this.selectedCustomizations.filter(cust => this.MonthyChargesQuery(cust));
        const hideMainPackage = monthlyCustomizations.some(cust => cust.customization.Choices?.Choice?.length > 0);
        const packagePrice = hideMainPackage ? 0 : videoComponent?.Price || 0;
        const videoCartData: VideoCartData = {
            hideMainPackage,
            name: videoComponent?.ComponentName || '',
            price: packagePrice,
            promoPrice: hideMainPackage ? 0 : videoComponent?.PromoPrice || 0,
            monthly: {
                totalAmount: packagePrice,
                items: [],
                promotions: {
                    totalAmount: 0,
                    items: []
                }
            }
        };

        if (videoOQComponent) {
            videoCartData.monthly.totalTaxAmount = videoOQComponent.MonthlyCharges?.TaxAmount || '0';
            videoCartData.monthly.totalAmount += parseFloat(videoCartData.monthly.totalTaxAmount);
        }

        for (const offer of monthlyCustomizations) {
            for (const choice of offer.customization.Choices.Choice) {
                const cartItem: CartItem = {
                    disclosure: choice.CartDisclosure || '',
                    amount: choice.Fee.Fee || 0,
                    name: choice.Name || '',
                    choiceCode: choice.ChoiceCode || '',
                    choice: choice,
                    customization: offer.customization,
                    metadata: null,
                    cartSectionType: CartSectionType.Monthly,
                    type: CartItemType.Fee,
                    productType: ProductType.Video,
                    CartDisclosureList: this.getCartDisclosureList(choice),
                    excludeFromProductSummary: choice.ExcludeFromProductSummary?.toLocaleLowerCase() === 'true'
                }

                if (cartItem.amount < 0) {
                    cartItem.type = CartItemType.Promotion; //Make it promotion if amount is negative
                    videoCartData.monthly.promotions.items.push(cartItem);
                    videoCartData.monthly.promotions.totalAmount += cartItem.amount;
                } else {
                    cartItem.type = CartItemType.Fee;
                    videoCartData.monthly.items.push(cartItem);
                    videoCartData.monthly.totalAmount += cartItem.amount;
                }
            }

        }

        videoCartData.monthly.totalAmount += videoCartData.monthly.promotions.totalAmount || 0;

        return videoCartData;
    }


    isCartEmpty = computed(() => {
        return !this._cart();
    })

    getTotalTodaysCharges(): number {
        const cart = this.cart();
        const requiredTodaysCharges = cart?.todaysCharges?.items?.filter(item => item.isRequired === true || item.isRequired === null || item.isRequired === undefined);
        return requiredTodaysCharges?.reduce((total, item) => total + item.amount, 0) || 0;
    }
}
